import { getPublicEvents } from "../services/EventMasterService";

/**
 * Generates JSON-LD schema markup for events to help Google index them
 * This runs on app initialization to fetch active/upcoming events and add structured data
 */
class SEOEventSchema {
  static async initializeEventSchema() {
    try {
      // Fetch active/upcoming events from DB
      const response = await getPublicEvents({ status: "Confirmed" });
      const events = Array.isArray(response?.events) ? response.events : [];

      if (events.length === 0) return;

      // Filter for upcoming events only
      const now = new Date();
      const upcomingEvents = events.filter((event) => {
        const eventStart = new Date(event?.startDate);
        return eventStart > now;
      });

      if (upcomingEvents.length === 0) return;

      // Generate JSON-LD schema for each event
      const eventSchemas = upcomingEvents.map((event) =>
        SEOEventSchema.generateEventSchema(event)
      );

      // Create a BreadcrumbList schema
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://graceanointing.org",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Events",
            item: "https://graceanointing.org/events",
          },
        ],
      };

      // Create Organization schema
      const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Grace Anointing Catholic Ministries",
        url: "https://graceanointing.org",
        logo: "https://www.graceanointing.org/images/logo.svg",
        description:
          "Grace Anointing Catholic Ministries offers global deliverance retreats, healing conventions, and evangelization programs.",
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "Customer Support",
          email: "retreat@graceanointing.org",
        },
      };

      // Inject all schemas into the document head
      SEOEventSchema.injectSchema(organizationSchema);
      SEOEventSchema.injectSchema(breadcrumbSchema);
      eventSchemas.forEach((schema) => SEOEventSchema.injectSchema(schema));

      console.log(`Added SEO schemas for ${upcomingEvents.length} upcoming events`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Failed to initialize event schema:", error);
      }
    }
  }

  static generateEventSchema(event) {
    const startDate = new Date(event?.startDate);
    const endDate = new Date(event?.endDate);
    const registrationStart = new Date(event?.regDate);
    const registrationEnd = new Date(event?.regEndDate);

    // Determine event status
    const now = new Date();
    let eventStatus = "EventScheduled";
    if (now > endDate) {
      eventStatus = "EventCancelled";
    } else if (now > startDate && now < endDate) {
      eventStatus = "EventInProgress";
    }

    // Build location object
    const location = {
      "@type": "Place",
      name: event?.location || "TBD",
    };
    if (event?.address) {
      location.address = {
        "@type": "PostalAddress",
        streetAddress: event.address,
        addressCountry: "US", // You can make this dynamic if needed
      };
    }
    if (event?.map) {
      location.url = event.map;
    }

    // Build price specifications if age groups exist
    const priceSpecifications = [];
    if (Array.isArray(event?.ageGroups) && event.ageGroups.length > 0) {
      event.ageGroups.forEach((group) => {
        if (group.rate > 0) {
          priceSpecifications.push({
            "@type": "PriceSpecification",
            priceCurrency: group.currency || "USD",
            price: group.rate.toString(),
            eligibleQuantity: {
              "@type": "QuantitativeValue",
              unitCode: "QT",
              minValueInclusive: group.minAge,
              maxValueInclusive: group.maxAge,
            },
            description: group.ageGroupName || "General",
          });
        }
      });
    }

    // Build the event schema
    const eventSchema = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: event?.name || "Upcoming Event",
      description: event?.description || event?.shortDescription || "",
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      eventStatus: `https://schema.org/${eventStatus}`,
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location,
      organizer: {
        "@type": "Organization",
        name: "Grace Anointing Catholic Ministries",
        url: "https://graceanointing.org",
      },
    };

    // Add offer if pricing info exists
    if (priceSpecifications.length > 0) {
      eventSchema.offers = priceSpecifications;
    }

    // Add registration details
    if (registrationStart && registrationEnd) {
      eventSchema.aggregateOffer = {
        "@type": "AggregateOffer",
        priceCurrency: "USD",
        offerCount: event?.maxRegistrationCount || "Unknown",
        availability: now < registrationEnd ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        validFrom: registrationStart.toISOString(),
        validThrough: registrationEnd.toISOString(),
      };
    }

    // Add image if available
    if (event?.brochurePath) {
      eventSchema.image = event.brochurePath;
    }

    return eventSchema;
  }

  static injectSchema(schema) {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  }
}

export default SEOEventSchema;
